import { FC, InputHTMLAttributes, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>

/**
 * Password field with a reveal toggle, for the load-hub loadboard auth forms
 * (`LoadboardAuthDialog`, `AihubAuthModal`).
 *
 * Takes the caller's field styling through `className` — each modal owns its own
 * `inputClass` — so the field stays visually identical to the email/username inputs
 * beside it. Deliberately NOT built on `components/ds2/TextField` (which already has
 * this toggle): that is a DS v2 component with v2 token vocabulary — pill radius,
 * borderless `shadow-field`, `bg-surface` — and these modals are still legacy
 * vocabulary (`gray-*` + `dark:slate-*`, `rounded-md`, MUI Dialog). Dropping a v2
 * field into a v1 modal would both look wrong and mix the two vocabularies, which the
 * numeo-ds-v2 skill forbids. When these modals migrate to v2, delete this and use
 * `TextField inputType="password"` instead.
 */
export const PasswordInput: FC<PasswordInputProps> = ({ className = '', disabled, ...props }) => {
    const [visible, setVisible] = useState(false)

    return (
        <div className="relative flex w-full items-center">
            <input
                {...props}
                type={visible ? 'text' : 'password'}
                disabled={disabled}
                // `pr-9` reserves room for the toggle. It follows the caller's `px-3` in the
                // class list, and Tailwind emits `pr-*` after `px-*`, so the right padding
                // resolves to the wider value regardless of attribute order.
                className={`${className} w-full pr-9`}
            />
            <button
                type="button"
                // Kept out of the tab order so Tab still runs input → submit, matching
                // `components/ds2/TextField`.
                tabIndex={-1}
                disabled={disabled}
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? 'Hide password' : 'Show password'}
                aria-pressed={visible}
                title={visible ? 'Hide password' : 'Show password'}
                className="absolute right-0 flex h-full w-9 items-center justify-center text-gray-400 transition-colors hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-500 dark:hover:text-slate-300"
            >
                {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
        </div>
    )
}
